import axios from 'axios'
import store from '@/store'
import { createObjectURL, getLocationFromEXIF, formatLocation, reverseGeo } from '@/util'

let appCredentials = false

export default class Draft {
  fileErrors = []
  uploadErrors = []
  content = null
  uploadingMedia = false
  uploadingNote = false
  progress = 0
  noteSubmitted = false
  previewUrl = null; // last semi to keep highlighting

  constructor (data = {}) {
    this.draftKey = data.draftKey || new Date().getTime()
    this.title = data.title
    this.status = data.status || 'publish'
    this.file = data.file
    this.text = data.text
    this.taxonomies = data.taxonomies || {
      method: [],
      keyword: [],
      folksonomy: []
    }
    this.mediaInfo = data.mediaInfo
    this.linkedProjects = data.linkedProjects || []
    this.resourceType = data.resourceType || 'note'

    this.latitude = data.latitude
    this.longitude = data.longitude
    this.location = data.location
  }

  /*
  * methods
  */

  copy () {
    // return a copy for editing etc
    return Object.assign({}, this)
  }

  setFile (data) {
    this.file = data // Blob, File`
  }

  async setLocation (deviceLocation, ignoreSaved = false) {
    try {
      if (this.file !== undefined && !ignoreSaved) {
        const exifLoc = await getLocationFromEXIF(this.file)
        if (exifLoc !== null) {
          this.latitude = exifLoc.latitude
          this.longitude = exifLoc.longitude
        }
      }

      if ((!this.hasLocation || ignoreSaved) && deviceLocation !== null) {
        this.latitude = deviceLocation.coords.latitude
        this.longitude = deviceLocation.coords.longitude
      }

      if (this.hasLocation) {
        await this.getReverseGeo()
      }
    } catch (err) {
      throw err
    }
  }

  async setManualLocation (geo = {}) {
    this.latitude = geo.latitude
    this.longitude = geo.longitude
    // format as below (city, countryCode)
    this.location = formatLocation(geo.address)
  }

  async getReverseGeo () {
    if (!this.latitude || !this.longitude) return

    const location = await reverseGeo(this.latitude, this.longitude)
    this.location = location
  }

  addProject (project) {
    let p = this.linkedProjects.findIndex((d) => d.id === project.id)
    if (p > -1) {
      this.linkedProjects.splice(p, 1)
      return
    }
    this.linkedProjects.push(project)
  }
  removeProject (id) {
    let p = this.linkedProjects.findIndex((d) => d.id === id)
    if (p > -1) {
      this.linkedProjects.splice(p, 1)
    }
  }

  async upload (credentials) {
    this.clearErrors()
    if (!credentials) {
      this.uploadErrors.unshift(new Error('Unauthorized'))
      return
    }
    appCredentials = credentials

    // console.log('starting upload for', this.draftKey)

    if (this.file !== undefined) {
      try {
        await this.uploadMedia()
      } catch (err) {
        this.fileErrors.unshift(err)
        this.progress = 0
        this.uploadingMedia = false
        return
      }
    }

    try {
      await this.uploadNote()
    } catch (err) {
      this.uploadErrors.unshift(err)
      this.progress = 0
      this.uploadingNote = false
      return
    }
  }

  async uploadMedia () {
    // skip this if we've already uploaded the media
    if (this.mediaInfo && this.mediaInfo.id) return

    const postUrl = process.env.VUE_APP_API_ROOT + 'media'

    // Build formData for upload
    const formData = new FormData()
    formData.append('file', this.file)
    formData.append('title', this.file.name)
    formData.append('text', this.text || '')

    this.uploadingMedia = true

    const res = await axios(postUrl, {
      method: 'post',
      headers: {
        'Content-Disposition': `form-data; filename="${ this.file.name }"`,
        'Authorization': `Basic ${appCredentials}`
      },
      data: formData,
      onUploadProgress: (e) => {
        this.progress = Math.round((e.loaded * 100) / e.total)
      }
    })

    this.mediaInfo = res.data
    this.uploadingMedia = false
  }

  async uploadNote () {
    // console.log('uploadNote :)')
    const postUrl = process.env.VUE_APP_API_ROOT + 'field_note'

    const title = `${new Date().getTime()}-${store.state.userInfo.name}`

    const formData = new FormData()
    formData.append('title', title)
    formData.append('fields[text]', this.text)
    formData.append('status', 'publish')
    formData.append('fields[latitude]', this.latitude)
    formData.append('fields[longitude]', this.longitude)
    formData.append('fields[location]', this.location)

    if (this.mediaInfo && this.mediaInfo.id) {
      // append the media field
      formData.append('fields[media]', this.mediaInfo.id)
    }

    if (this.method.length) {
      for (let m of this.method) {
        formData.append('method[]', m)
      }
    }
    if (this.keyword.length) {
      for (let k of this.keyword) {
        formData.append('keyword[]', k)
      }
    }
    if (this.folksonomy.length) {
      for (let f of this.folksonomy) {
        formData.append('folksonomy[]', f)
      }
    }
    if (this.linkedProjects.length) {
      for (let p of this.linkedProjects) {
        formData.append('fields[linked_projects][]', p.id)
      }
    }

    this.uploadingNote = false

    const res = await axios(postUrl, {
      method: 'post',
      headers: {
        'Authorization': `Basic ${appCredentials}`
      },
      data: formData,
      onUploadProgress: (e) => {
        this.progress = Math.round((e.loaded * 100) / e.total)
      }
    })

    if (res.status > 204) {
      throw new Error(res.status)
    }

    this.uploadingNote = false
    this.noteSubmitted = res.data
  }

  createPreviewUrl () {
    if (!this.file) return
    this.previewUrl = createObjectURL(this.file)
  }

  blobToFile () {
    if (!this.file) return
    if (this.file.constructor.name === 'Blob') {
      this.file = new File([this.file], this.file.name, {
        type: this.file.type
      })
    }
  }

  clearErrors () {
    this.fileErrors = []
    this.uploadErrors = []
  }

  /*
  * getters
  */

  get method () {
    return this.taxonomies.method.map(({ id }) => id)
  }

  get keyword () {
    return this.taxonomies.keyword.map(({ id }) => id)
  }

  get folksonomy () {
    return this.taxonomies.folksonomy.map(({ id }) => id)
  }

  get isVideo () {
    return !!this.file && this.file.type.indexOf('video') > -1
  }
  get isAudio () {
    return !!this.file && this.file.type.indexOf('audio') > -1
  }
  get isImage () {
    return !!this.file && this.file.type.indexOf('image') > -1
  }
  get hasFile () {
    return this.isVideo || this.isAudio || this.isImage
  }

  get hasLocation () {
    return this.latitude !== undefined && this.longitude !== undefined
  }

  get projects () {
    if (!this.linkedProjects.length) return ''
    return this.linkedProjects.map(p => p.title).join(', ')
  }

  get processing () {
    return this.uploadingMedia || this.uploadingNote
  }

  get fileType () {
    if (!this.file) return null
    if (this.file.type === 'video/quicktime') return 'video/mp4'
    return this.file.type
  }

  get fileSize () {
    if (!this.file) return 0
    return this.file.size
  }

  get hasErrors () {
    return (this.fileErrors.length || this.uploadErrors.length) > 0
  }
}
